import { FC, useEffect, useState } from 'react'
import { Menu, Layout, Tooltip } from 'antd'
import { useLocation, useNavigate } from 'react-router'
import { Link } from 'react-router-dom'
import { ADMIN_PREFIX_PATH, APP_PREFIX_PATH } from 'config'
import { baseAPI, useManageWebsiteMutation } from 'api/baseApi'
import { useWebSettings } from 'raducer/webSettings'
import { useAppDispatch } from 'hooks'
import { useLeadFilter } from 'features/leads/slice/leadSlice'
import {
 SvgAlertIcon,
 SVGContactUseIcon,
 SvgCrmIcon,
 SvgNewListingIcon,
 SvgSettingsIcon,
} from 'components/icons'
import { ItemType } from 'antd/lib/menu/hooks/useItems'
import { useGlobal } from 'raducer/global'
import useCheckModule from 'hooks/useCheckModule'
import { TModuleType } from 'types'

// Menu item with optional module slug(s); hidden when the module is disabled
type SidebarLinksType = ItemType & {
 mSlug?: TModuleType | TModuleType[]
 children?: SidebarLinksType[]
}

// External AgentRoof CRM help/wiki documentation
const HELP_WIKI_URL = 'https://wiki.agentroof.com/books/agentroof-crm'

const Sidebar: FC<{
 collapsed: boolean
 //  sidebarLInk: SidebarLinksType[]
}> = ({
 collapsed,
 // sidebarLInk
}) => {
 const { global } = useGlobal()
 const { checkIsModule } = useCheckModule()
 const { pathname } = useLocation()
 const navigate = useNavigate()
 const d = useAppDispatch()
 const { onboarding_settings } = useWebSettings()
 const { onBoardListingStatus } = onboarding_settings || {}
 const { updateRecentLeadData, recentLeadData } = useLeadFilter()
 const [manageWebsiteMutation] = useManageWebsiteMutation()
 const [openKeys, setOpenKeys] = useState<string[]>([])

 const manageWebsite = () => {
  manageWebsiteMutation?.()
   .then((res: any) => {
    if (!onBoardListingStatus?.manage_website) {
     d(baseAPI.util.invalidateTags(['WEB-SETTINGS']))
    }
    if (res?.data?.url) {
     window.open(res.data.url, '_blank')
    }
   })
   .catch((err) => {
    console.log(err)
   })
 }

 //  const [openKeys, setOpenKeys] = useState([]);
 const [selectedKeys, setSelectedKeys] = useState<any>([])

 const getParentKey = (pathname: string) => {
  const sections = [
   'alert',
   'leads',
   'listing',
   'automation',
   'settings',
   'templates',
   'reports',
  ]
  const matchedSection = sections.find((section) =>
   pathname?.includes(`/${section}/`),
  )

  return matchedSection ? `${APP_PREFIX_PATH}/${matchedSection}` : pathname
 }

 useEffect(() => {
  if (pathname) {
   const parentKey = getParentKey(pathname)
   setSelectedKeys([pathname, parentKey].filter(Boolean))
   if (collapsed) {
    setOpenKeys([])
   } else {
    setOpenKeys([parentKey])
   }
  }
 }, [pathname, collapsed])

 const {
  user_type_sold,
  listing_plan,
  listing_key,
  brokerage_data,
  open_house,
 } = global || {}

 const SidebarLinks: SidebarLinksType[] = [
  {
   icon: <i className="fas fa-chart-line" />,
   key: `${APP_PREFIX_PATH}/dashboard`,
   label: 'Dashboard',
  },
  {
   icon: (
    <span>
     <SvgCrmIcon />
    </span>
   ),
   key: `${APP_PREFIX_PATH}/leads`,
   label: 'CRM',
  },
  ...(listing_key
   ? [
      {
       icon: (
        <span>
         <SvgNewListingIcon />
        </span>
       ),
       key: `${APP_PREFIX_PATH}/listing`,
       label: 'Listings',
       children: [
        ...(listing_plan !== 'Pre-Condo'
         ? [
            {
             label: 'Property Listings',
             key: `${APP_PREFIX_PATH}/listing/property-listing`,
             mSlug: 'listing_mls' as TModuleType,
            },
           ]
         : []),
        {
         label: (
          <Tooltip overlay="Pre-Construction Listings">
           Pre-Construction Listings
          </Tooltip>
         ),
         key: `${APP_PREFIX_PATH}/listing/pre-con-listing`,
         mSlug: 'listing_precon' as TModuleType,
        },
        ...((brokerage_data?.length ?? 0) > 0
         ? [
            {
             label: 'Brokerage Listings',
             key: `${APP_PREFIX_PATH}/listing/brokerage-listing`,
             mSlug: 'listing_brokerage_listings' as TModuleType,
            },
           ]
         : []),
       ],
      },
     ]
   : []),
  ...(listing_plan !== 'Pre-Condo'
   ? [
      {
       icon: (
        <span>
         <SvgAlertIcon />
        </span>
       ),
       key: `${APP_PREFIX_PATH}/alert`,
       label: 'Alert',
       children: [
        {
         label: ' New Listing Alert',
         key: `${APP_PREFIX_PATH}/alert/new-listing`,
         mSlug: 'alert_new_listing' as TModuleType,
        },
        ...(user_type_sold
         ? [
            {
             label: (
              <Tooltip overlay="Neighbourhood Alert">
               Neighbourhood Alert
              </Tooltip>
             ),
             key: `${APP_PREFIX_PATH}/alert/sold-listing`,
             mSlug: 'alert_neighbourhood' as TModuleType,
            },
           ]
         : []),
        ...(open_house
         ? [
            {
             label: 'Open House Alert',
             key: `${APP_PREFIX_PATH}/alert/open-house-listing`,
             mSlug: 'alert_open_house' as TModuleType,
            },
           ]
         : []),
        {
         label: 'Price Drop Alert',
         key: `${APP_PREFIX_PATH}/alert/price-drop`,
         mSlug: 'alert_price_drop' as TModuleType,
        },
       ],
      },
     ]
   : []),
  {
   icon: (
    <span className="ai-hub-sparkle-icon">
     <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
       d="M8 1L9.5 6H14.5L10.5 9L12 14L8 11L4 14L5.5 9L1.5 6H6.5L8 1Z"
       fill="white"
      />
     </svg>
    </span>
   ),
   key: `${APP_PREFIX_PATH}/automation`,
   className: 'ai-hub-menu-item',
   label: (
    <span className="ai-hub-label-group">
     <span className="ai-hub-label-text">AI Hub</span>
    </span>
   ),
   children: [
    {
     label: 'AI Listing Intelligence',
     key: `${APP_PREFIX_PATH}/automation/ai-listing-intelligence`,
     mSlug: 'ai_hub_listing_intelligence',
    },
    {
     label: 'AI Lead Engagement',
     key: `${APP_PREFIX_PATH}/automation/ai-lead-engagement-intelligence`,
     mSlug: 'ai_hub_lead_engagement',
    },
    {
     label: 'Action Plan',
     key: `${APP_PREFIX_PATH}/automation/action-plan`,
     mSlug: 'ai_hub_action_plan',
    },
    {
     label: 'Market Updates',
     key: `${APP_PREFIX_PATH}/automation/market-updates`,
     mSlug: 'ai_hub_market_update',
    },
    {
     label: 'Real Estate Newsletter',
     key: `${APP_PREFIX_PATH}/automation/real-estate-news`,
     mSlug: 'ai_hub_real_estate_news',
    },
   ],
  },
  {
   icon: <i className="fas fa-phone-alt" />,
   key: `${APP_PREFIX_PATH}/calls`,
   label: (
    <span className="ai-hub-label-group">
     <span>Calls</span>
     <span className="ai-hub-new-badge">New</span>
    </span>
   ),
  },
  {
   icon: (
    <span>
     <SvgSettingsIcon />
    </span>
   ),
   key: `${APP_PREFIX_PATH}/settings`,
   label: 'Settings',
   children: [
    {
     label: 'Tags',
     key: `${APP_PREFIX_PATH}/settings/tags`,
    },
    {
     label: 'Stages',
     key: `${APP_PREFIX_PATH}/settings/stages`,
    },
    {
     label: 'Sources',
     key: `${APP_PREFIX_PATH}/settings/sources`,
    },
    {
     label: 'Signature',
     key: `${APP_PREFIX_PATH}/settings/signature`,
    },
    ...(listing_key
     ? [
        {
         label: 'Listing Settings',
         key: `${APP_PREFIX_PATH}/settings/listing-settings`,
         mSlug: 'settings_listing_settings' as TModuleType,
        },
       ]
     : []),
    {
     label: 'Billing Portal',
     key: `${APP_PREFIX_PATH}/settings/billing-portal`,
    },
   ],
  },
  {
   icon: <i className="fas fa-file-chart-line" />,
   key: `${APP_PREFIX_PATH}/templates`,
   label: 'Templates',
   children: [
    {
     label: 'SMS Templates',
     key: `${APP_PREFIX_PATH}/templates/text-templates`,
    },
    {
     label: 'Email Templates',
     key: `${APP_PREFIX_PATH}/templates/email-templates`,
    },
    {
     label: 'Birthday Wishes',
     key: `${APP_PREFIX_PATH}/templates/birthday-wishes`,
    },
   ],
  },
  {
   label: 'Integration',
   key: `${APP_PREFIX_PATH}/integration`,
   mSlug: 'integration',
   icon: <i className="fas fa-link" />,
  },
  {
   icon: <i className="fas fa-chart-pie" />,
   key: `${APP_PREFIX_PATH}/reports`,
   label: 'Report',
   children: [
    {
     label: 'Email Reports',
     key: `${APP_PREFIX_PATH}/reports/email-reports`,
    },
    {
     label: 'SMS Reports',
     key: `${APP_PREFIX_PATH}/reports/sms-reports`,
    },
   ],
  },
  {
   icon: <i className="fas fa-solid fa-globe" />,
   key: 'manage-website',
   label: 'Manage Website',
   mSlug: 'manage_website',
  },
  {
   icon: <i className="fas fa-question-circle" />,
   key: 'help',
   label: 'Help',
  },
  {
   icon: <SVGContactUseIcon className="text-black" />,
   key: 'contact-us',
   label: 'Contact Us',
  },
 ]

 // Filter menu by enabled modules (recursive); drop parents left with no children
 const filterByModule = (links: SidebarLinksType[]): ItemType[] =>
  links.reduce<ItemType[]>((acc, link) => {
   if (!link || !checkIsModule(link.mSlug)) return acc
   // strip `mSlug` so it isn't passed down to antd Menu / DOM
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
   const { mSlug, children, ...rest } = link
   if (children) {
    const allowedChildren = filterByModule(children)
    if (allowedChildren.length === 0) return acc
    acc.push({ ...rest, children: allowedChildren } as ItemType)
   } else {
    acc.push(rest as ItemType)
   }
   return acc
  }, [])

 const allowedMenus = filterByModule(SidebarLinks)

 return (
  <Layout.Sider theme="light" className="crmSidebar" collapsed={collapsed}>
   <Link
    onClick={() => {
     if (recentLeadData) {
      updateRecentLeadData?.(null)
     }
    }}
    to={`${ADMIN_PREFIX_PATH}/dashboard`}
   >
    <div className="logo">
     <img src="/logo-sm.png" alt="" />
    </div>
   </Link>

   <Menu
    selectable
    // selectedKeys={pathname ? [pathname] : []}
    selectedKeys={selectedKeys}
    onClick={(e) => {
     // Help always opens the external wiki in a new tab, keeping the CRM open
     if (e?.key === 'help') {
      window.open(HELP_WIKI_URL, '_blank', 'noopener,noreferrer')
     } else if (e?.domEvent?.ctrlKey && e?.key !== 'manage-website') {
      window.open(e?.key, '_blank')
     } else if (e?.key === 'manage-website') {
      manageWebsite()
     } else if (e?.key === `${APP_PREFIX_PATH}/dashboard` && recentLeadData) {
      updateRecentLeadData?.(null)
     } else {
      navigate(e?.key)
     }
    }}
    rootClassName={`sidebar-menu ${collapsed ? 'collapsed-sidebar' : ''}`}
    theme="light"
    items={allowedMenus}
    mode="inline"
    openKeys={openKeys}
    onOpenChange={(v) => {
     setOpenKeys([v?.at(-1) ?? ''])
    }}
   />
  </Layout.Sider>
 )
}

export default Sidebar
